import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import HomePage from './pages/HomePage';
import LoadingScreen from './pages/LoadingScreen';
import ReviewPage from './pages/ReviewPage';
import ConfirmationPage from './pages/ConfirmationPage';
import PreviewPage from './pages/PreviewPage';
import CommunityPage from './pages/CommunityPage';
import ConfirmViolationPage from './pages/ConfirmViolationPage';
import './App.css';

/**
 * @file App.tsx
 * @description Main application component, sets up routing and global providers.
 *
 * @note For environment variables to work, create a `.env` file in the root directory
 *       and add your Google Client ID like this:
 *       REACT_APP_GOOGLE_CLIENT_ID=YOUR_GOOGLE_CLIENT_ID_HERE
 */

// Fetch Google Client ID from environment variables
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

if (!GOOGLE_CLIENT_ID) {
  console.error("FATAL ERROR: REACT_APP_GOOGLE_CLIENT_ID is not defined.");
  // In a real app, you might want to render an error page here.
}

/**
 * AppContent component manages the layout and routing.
 * It conditionally renders the navbar based on the current route.
 */
const AppContent: React.FC = () => {
  const location = useLocation();
  const isCommunityPage = location.pathname === '/community';
  const isConfirmViolationPage = location.pathname === '/confirm-violation';

  // These pages have their own self-contained UI and don't need the main navbar
  const shouldShowNavbar = !isCommunityPage && !isConfirmViolationPage;

  return (
    <div className="App">
      {shouldShowNavbar && (
        <nav className="navbar">
          <Link to="/" className="logo-group" style={{ textDecoration: 'none', color: 'inherit' }}>
            <div className="logo">RTM</div>
            <div className="subtitle">report this m*</div>
          </Link>
        </nav>
      )}
      <main className="main-container">
        <Routes>
          {/* Main user flow for reporting */}
          <Route path="/" element={<HomePage />} />
          <Route path="/loading" element={<LoadingScreen />} />
          <Route path="/preview" element={<PreviewPage />} />
          <Route path="/review" element={<ReviewPage />} />
          <Route path="/confirmation" element={<ConfirmationPage />} />

          {/* Community and violation confirmation flow */}
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/confirm-violation" element={<ConfirmViolationPage />} />
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

function App() {
  // The GoogleOAuthProvider must be initialized with a client ID.
  // If it's not available, we can show a loading/error state,
  // but for now, we proceed, and the Google login will fail gracefully.
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID || ''}>
      <Router>
        <AppContent />
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;
